import {
  PROVIDER_META,
  PROVIDER_TYPES,
  type ProviderAuthMode,
  type ProviderDto,
  type ProviderType,
} from '@af/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ClaudeLoginDialog } from '@/components/provider/ClaudeLoginDialog';
import { CodexConnection } from '@/components/provider/CodexConnection';
import { Button } from '@/components/ui/button';
import { IconCheck, ProviderIcon } from '@/components/ui/icons';
import { Field, Input } from '@/components/ui/input';
import { ctaCls } from '@/components/ui/link-styles';
import { Badge, Banner } from '@/components/ui/misc';
import { call } from '@/lib/api';
import { qk, useMe } from '@/lib/queries';
import { cn } from '@/lib/utils';

export type GoNext = () => void;

export function StepShell({
  title,
  desc,
  children,
  footer,
}: {
  title: ReactNode;
  desc: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-medium tracking-tight text-fg">{title}</h2>
        <p className="mt-1 text-sm text-muted">{desc}</p>
      </div>
      {children}
      {footer ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">{footer}</div>
      ) : null}
    </section>
  );
}

export function uniqueSlug(base: string, taken: string[]) {
  if (!taken.includes(base)) return base;
  let i = 2;
  while (taken.includes(`${base}-${i}`)) i++;
  return `${base}-${i}`;
}

/** Connect a provider: test it and sync its models (guided setup and every "provider first" gate). */
export function ProviderSetup({ onDone, providers }: { onDone: GoNext; providers: ProviderDto[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [type, setType] = useState<ProviderType>('anthropic');
  const meta = PROVIDER_META[type];
  const [created, setCreated] = useState<ProviderDto | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [subToken, setSubToken] = useState('');
  const [moved, setMoved] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [chatgpt, setChatgpt] = useState(false);
  const [codexFor, setCodexFor] = useState<ProviderDto | null>(null);
  const synced = useRef<string | null>(null);
  const me = useMe();
  // Claude subscription (server switch): Anthropic can use an API key, a subscription token or both.
  const subscriptionOn = type === 'anthropic' && !!me.data?.features?.claudeSubscription;
  // Own Claude login (hosted): pay per token with a key, or let Claude Code sign in with your own account.
  const loginOn = type === 'anthropic' && !subscriptionOn && !!me.data?.features?.claudeLogin;
  const [pay, setPay] = useState<'key' | 'login'>('key');
  const viaLogin = loginOn && pay === 'login';
  const [loginFor, setLoginFor] = useState<ProviderDto | null>(null);
  // Re-use an existing provider of this type (a failed attempt) instead of creating `<type>-2`.
  // Prefer the fresh copy from the providers query (the ChatGPT sign-in changes the provider server-side).
  const existing =
    (created?.type === type ? (providers.find((x) => x.id === created.id) ?? created) : null) ??
    providers.find((x) => x.type === type) ??
    null;
  // ChatGPT subscription (server switch): OpenAI can be paid with an API key or a ChatGPT sign-in.
  const codexOn = type === 'openai' && !!me.data?.features?.codexSubscription;
  const viaChatgpt = codexOn && chatgpt;
  const hasKey = !!apiKey || !!existing?.apiKeyHint;
  const hasToken = subscriptionOn && (!!subToken || !!existing?.subscriptionTokenHint);
  const authMode: ProviderAuthMode = viaLogin
    ? hasKey
      ? 'hybrid_login'
      : 'sandbox_login'
    : hasToken
      ? hasKey
        ? 'hybrid'
        : 'subscription'
      : 'api_key';
  const onKeyChange = (v: string) => {
    // A setup-token pasted into the API key field: move it where it belongs.
    if (subscriptionOn && v.trim().startsWith('sk-ant-oat')) {
      setSubToken(v.trim());
      setApiKey('');
      setMoved(true);
      return;
    }
    setApiKey(v);
  };
  const [phase, setPhase] = useState<'idle' | 'testing' | 'syncing'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<number | null>(null);

  const connect = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPhase('testing');
    try {
      let p = existing;
      const auth = subscriptionOn
        ? { authMode, ...(subToken ? { subscriptionToken: subToken.trim() } : {}) }
        : loginOn
          ? { authMode }
          : {};
      if (!p) {
        p = await call('createProvider', {
          body: {
            slug: uniqueSlug(
              type.replace(/_/g, '-'),
              providers.map((x) => x.slug),
            ),
            type,
            name: meta.label,
            apiKey: apiKey || undefined,
            baseUrl: meta.needsBaseUrl ? baseUrl || meta.defaultBaseUrl : undefined,
            enabled: true,
            ...auth,
          },
        });
        setCreated(p);
      } else if (apiKey || baseUrl || subToken || ((subscriptionOn || loginOn) && p.authMode !== authMode)) {
        p = await call('updateProvider', {
          params: { id: p.id },
          body: { ...(apiKey ? { apiKey } : {}), ...(baseUrl ? { baseUrl } : {}), ...auth },
        });
        setCreated(p);
      }
      void qc.invalidateQueries({ queryKey: qk.providers });
      // A subscription token can't be tested through the API (checked on the first Claude Code run).
      // The own-login mode is tested by signing in (next step), not with a key.
      const test =
        p.authMode === 'subscription' || p.authMode === 'sandbox_login'
          ? { ok: true, error: null }
          : await call('testProvider', { params: { id: p.id }, body: {} });
      if (!test.ok) {
        setError(t('onboarding.testFailed', { error: test.error ?? 'unknown error' }));
        setPhase('idle');
        return;
      }
      await syncModels(p);
      // Own account chosen: continue straight into Claude Code's sign-in.
      if (viaLogin) setLoginFor(p);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPhase('idle');
    }
  };

  const syncModels = async (p: ProviderDto) => {
    setPhase('syncing');
    const sync = await call('syncModels', { params: { id: p.id }, body: {} });
    // Wait for fresh counts: "Next step" decides which step is still open from the onboarding status.
    await Promise.all([
      qc.invalidateQueries({ queryKey: qk.models }),
      qc.invalidateQueries({ queryKey: qk.onboarding }),
    ]);
    setResult(sync.total);
    toast.success(t('onboarding.connected', { count: sync.total }));
  };

  // Once the ChatGPT sign-in has finished (the provider is now in subscription mode), fetch its models.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run only when the connection state changes
  useEffect(() => {
    if (existing?.type !== 'openai' || existing.authMode !== 'subscription') return;
    if (!existing.subscriptionTokenHint || synced.current === existing.id) return;
    synced.current = existing.id;
    setError(null);
    void syncModels(existing)
      .catch((err) => setError((err as Error).message))
      .finally(() => setPhase('idle'));
  }, [existing?.id, existing?.authMode, existing?.subscriptionTokenHint]);

  /** ChatGPT: save the provider without a key, then sign in (device code) right here. */
  const connectChatgpt = async () => {
    setError(null);
    setPhase('testing');
    try {
      const p =
        existing ??
        (await call('createProvider', {
          body: {
            slug: uniqueSlug(
              'openai',
              providers.map((x) => x.slug),
            ),
            type: 'openai',
            name: meta.label,
            enabled: true,
          },
        }));
      setCreated(p);
      await qc.invalidateQueries({ queryKey: qk.providers });
      setCodexFor(p);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPhase('idle');
    }
  };

  return (
    <StepShell
      title={t('onboarding.steps.provider')}
      desc={t('onboarding.desc.provider')}
      footer={
        result != null || providers.length ? (
          <button type="button" className={ctaCls} onClick={onDone}>
            {t('onboarding.next')}
          </button>
        ) : null
      }
    >
      {providers.length ? (
        <ul className="flex flex-wrap gap-2" aria-label={t('settings.providers')}>
          {providers.map((p) => (
            <li key={p.id}>
              <Badge tone="success">
                <ProviderIcon type={p.type} size={12} /> {p.name} <IconCheck size={11} />
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (viaChatgpt) void connectChatgpt();
          else void connect(e);
        }}
        className="flex flex-col gap-4"
      >
        <fieldset>
          <legend className="mb-2 text-xs text-muted">{t('common.provider')}</legend>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {PROVIDER_TYPES.map((pt) => (
              <button
                key={pt}
                type="button"
                aria-pressed={type === pt}
                onClick={() => {
                  setType(pt);
                  setChatgpt(false);
                  setError(null);
                  setResult(null);
                }}
                className={cn(
                  'flex h-12 items-center gap-2.5 rounded-xl border px-3 text-left text-sm transition-colors',
                  type === pt
                    ? 'border-accent bg-accent/8 text-fg'
                    : 'border-border text-fg-2 hover:bg-hover',
                )}
              >
                <ProviderIcon type={pt} size={18} className="shrink-0" />
                <span className="truncate">{PROVIDER_META[pt].label}</span>
              </button>
            ))}
          </div>
        </fieldset>
        {loginOn ? (
          <fieldset className="flex flex-col gap-2 rounded-lg border border-border p-3">
            <legend className="px-1 text-xs text-muted">{t('onboarding.payTitle')}</legend>
            {(
              [
                ['key', t('onboarding.payKey'), t('onboarding.payKeyHint')],
                ['login', t('onboarding.payLogin'), t('onboarding.payLoginHint')],
              ] as const
            ).map(([value, label, hint]) => (
              <label key={value} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="ob-claude-pay"
                  checked={pay === value}
                  onChange={() => setPay(value)}
                  className="mt-1 accent-[var(--accent)]"
                />
                <span>
                  <span className="text-fg">{label}</span>
                  <span className="block text-xs text-muted">{hint}</span>
                </span>
              </label>
            ))}
            {viaLogin ? <p className="text-2xs text-muted">{t('providers.claudeLoginTerms')}</p> : null}
          </fieldset>
        ) : null}
        {codexOn ? (
          <fieldset className="flex flex-col gap-2 rounded-lg border border-border p-3">
            <legend className="px-1 text-xs text-muted">{t('providers.codexPay')}</legend>
            {(
              [
                [false, t('providers.payApi'), t('providers.payApiHint')],
                [true, t('providers.connectChatgpt'), t('providers.connectChatgptHint')],
              ] as const
            ).map(([on, label, hint]) => (
              <label key={label} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="ob-codex-pay"
                  checked={chatgpt === on}
                  onChange={() => setChatgpt(on)}
                  className="mt-1 accent-[var(--accent)]"
                />
                <span>
                  <span className="text-fg">{label}</span>
                  <span className="block text-xs text-muted">{hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}
        {meta.needsApiKey && !viaChatgpt ? (
          <Field
            label={subscriptionOn || viaLogin ? t('onboarding.apiKeyOptional') : t('onboarding.apiKey')}
            htmlFor="ob-key"
            hint={
              existing?.apiKeyHint
                ? `${existing.apiKeyHint} · ${t('onboarding.keyNote')}`
                : t('onboarding.keyNote')
            }
          >
            <Input
              id="ob-key"
              type="password"
              autoComplete="off"
              required={!existing && !hasToken && !viaLogin}
              value={apiKey}
              onChange={(e) => onKeyChange(e.target.value)}
              placeholder={type === 'anthropic' ? 'sk-ant-api03-…' : 'sk-…'}
            />
          </Field>
        ) : null}
        {type === 'anthropic' && meta.needsApiKey ? (
          <p className="-mt-1 text-2xs text-muted">
            {t('providers.maxCreditsHint')}{' '}
            <Link
              to="/help/a/$slug"
              params={{ slug: 'max-api-credits' }}
              className="text-accent hover:underline"
            >
              {t('providers.maxCreditsLink')}
            </Link>
          </p>
        ) : null}
        {subscriptionOn ? (
          <Field
            label={t('onboarding.subToken')}
            htmlFor="ob-sub"
            hint={
              existing?.subscriptionTokenHint
                ? `${existing.subscriptionTokenHint} · ${t('onboarding.subTokenHint')}`
                : t('onboarding.subTokenHint')
            }
          >
            <Input
              id="ob-sub"
              type="password"
              autoComplete="off"
              value={subToken}
              onChange={(e) => {
                setSubToken(e.target.value);
                setMoved(false);
              }}
              placeholder="sk-ant-oat01-…"
            />
          </Field>
        ) : null}
        {moved ? <Banner tone="info">{t('onboarding.subDetected')}</Banner> : null}
        {subscriptionOn && (hasKey || hasToken) ? (
          <p className="text-xs text-muted">{t(`onboarding.payMode.${authMode}`)}</p>
        ) : null}
        {meta.needsBaseUrl ? (
          <Field label={t('onboarding.baseUrl')} htmlFor="ob-url">
            <Input
              id="ob-url"
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder={meta.defaultBaseUrl ?? 'https://…/v1'}
            />
          </Field>
        ) : null}
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {result != null ? (
          <p
            role="status"
            className="rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm text-success"
          >
            <IconCheck size={13} className="inline" /> {t('onboarding.connected', { count: result })}
          </p>
        ) : null}
        <div>
          <Button type="submit" variant="primary" loading={phase !== 'idle'}>
            {phase === 'testing'
              ? t('onboarding.connecting')
              : phase === 'syncing'
                ? t('onboarding.syncing')
                : viaChatgpt
                  ? t('onboarding.signInChatgpt')
                  : t('onboarding.connect')}
          </Button>
        </div>
      </form>
      {codexFor ? <CodexConnection provider={codexFor} autoStart onClose={() => setCodexFor(null)} /> : null}
      {loginFor ? <ClaudeLoginDialog provider={loginFor} onClose={() => setLoginFor(null)} /> : null}
    </StepShell>
  );
}
